import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { AlertTriangle, Loader2, LocateFixed, MapPinOff, Radio, Square } from "lucide-react";

import { createClientRequestId } from "../../utils/clientRequestId";
import { riderApi } from "../services/riderApi";
import { getRiderDeviceInstanceId } from "../utils/riderDevice";
import {
  riderExecutionAuthoritySnapshot,
  riderExecutionHeaders,
  subscribeRiderExecutionAuthority,
} from "../utils/riderExecutionAuthority";
import {
  allocateTrackingSequence,
  clearTrackingSessionQueue,
  enqueueTrackingSample,
  listTrackingSamples,
  removeTrackingSamples,
} from "../tracking/deliveryTrackingQueue";

const DEFAULT_POLICY = Object.freeze({
  captureTargetSeconds: 10,
  uploadTargetSeconds: 15,
  heartbeatTargetSeconds: 30,
  batchMaximum: 100,
});

const STATE_COPY = Object.freeze({
  STARTING: ["Starting", "Preparing location sharing."],
  LIVE: ["Live", "Your recent location is being shared for the active stop."],
  DEGRADED: ["Degraded", "Location is less precise, but still usable."],
  LOCATION_UNAVAILABLE: ["Location unavailable", "Waiting for a usable location."],
  PERMISSION_DENIED: ["Permission denied", "Allow location access in your browser settings, then try again."],
  GPS_UNAVAILABLE: ["GPS unavailable", "Your device could not determine its location."],
  ACQUIRING_FIX: ["Finding your location",
    "This takes longer indoors or under cover. Move somewhere with a clear view of the sky."],
  OFFLINE_QUEUED: ["Offline / queued", "Locations are stored on this device until the connection returns."],
  REPLAYING: ["Replaying", "Sending saved locations in order."],
  QUEUE_FULL: ["Queue full / recovery required", "Reconnect before collecting more points. No valid saved point was discarded."],
  CONNECTION_INTERRUPTED: ["Connection interrupted", "The server has not heard from this device recently."],
  SESSION_EXPIRED: ["Session expired", "Start a new tracking session to continue."],
  TOKEN_LOST: ["Recovery required", "This device lost the session key. End the unusable session and restart safely."],
  AWAITING_DEVICE_CONTROL: ["Waiting for this device to take the trip",
    "Choose this device for the trip first. Location sharing belongs to whichever device is running it."],
  STOPPED: ["Stopped", "Location sharing is off."],
  ERROR: ["Error", "Location sharing needs your attention."],
});

function storageKeys(businessId, runId) {
  const suffix = `${Number(businessId)}:${Number(runId)}`;
  return { meta: `apparify:tracking:meta:${suffix}` };
}

function readJson(key) {
  try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; }
}

function randomSampleId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    return (character === "x" ? random : ((random & 0x3) | 0x8)).toString(16);
  });
}

export default function RiderTrackingCard({ businessId, run, onPosition, onTrackingState, required = false }) {
  const keys = useMemo(() => storageKeys(businessId, run?.id), [businessId, run?.id]);
  const [session, setSession] = useState(null);
  const [trackingState, setTrackingState] = useState("STOPPED");
  const [queueCount, setQueueCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(() => document.visibilityState === "visible");
  // A target-v2 run is driven by exactly one device, and tracking is one of the
  // things only that device may do. Subscribing rather than reading once matters:
  // this card is also mounted app-wide by RequiredTrackingProvider, where nothing
  // else re-renders it when the Rider claims the run on the run screen.
  const authoritySnapshot = useSyncExternalStore(
    subscribeRiderExecutionAuthority,
    () => riderExecutionAuthoritySnapshot(businessId, run?.id),
    () => "",
  );
  const controlsRun = Number(run?.workflowVersion) !== 2 || Boolean(authoritySnapshot);
  const tokenRef = useRef(null);
  const watchRef = useRef(null);
  const lastCaptureRef = useRef(0);
  const lastCommunicationRef = useRef(Date.now());
  const flushRef = useRef(false);
  const retryRef = useRef({ attempt: 0, timer: null });

  const persistSession = useCallback((next) => {
    setSession(next);
    if (next) localStorage.setItem(keys.meta, JSON.stringify(next));
    else localStorage.removeItem(keys.meta);
  }, [keys.meta]);

  const stopWatcher = useCallback(() => {
    if (watchRef.current != null && navigator.geolocation) navigator.geolocation.clearWatch(watchRef.current);
    watchRef.current = null;
  }, []);

  useEffect(() => {
    const meta = readJson(keys.meta);
    tokenRef.current = null;
    if (meta?.id && Number(meta.runId) === Number(run?.id)) {
      lastCommunicationRef.current = Number(meta.lastCommunicationAt || Date.now());
      setSession(meta);
      setTrackingState("TOKEN_LOST");
      listTrackingSamples(meta.id).then((rows) => setQueueCount(rows.length)).catch(() => setTrackingState("ERROR"));
    } else {
      setSession(null);
      setTrackingState("STOPPED");
    }
    return stopWatcher;
  }, [keys.meta, run?.id, stopWatcher]);

  useEffect(() => {
    const onVisibility = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const enqueuePosition = useCallback(async (position) => {
    if (!session?.id) return;
    const capturedMs = Number(position?.timestamp || Date.now());
    const captureEveryMs = Number(session.policy?.captureTargetSeconds || DEFAULT_POLICY.captureTargetSeconds) * 1000;
    if (capturedMs - lastCaptureRef.current < captureEveryMs) return;
    lastCaptureRef.current = capturedMs;
    try {
      const sequenceNo = await allocateTrackingSequence(session.id);
      const coordinates = position.coords;
      const sample = {
        clientSampleId: randomSampleId(),
        sequenceNo,
        latitude: Number(coordinates.latitude),
        longitude: Number(coordinates.longitude),
        accuracyM: Number(coordinates.accuracy),
        headingDegrees: Number.isFinite(coordinates.heading) ? Number(coordinates.heading) : null,
        speedMps: Number.isFinite(coordinates.speed) && coordinates.speed >= 0 ? Number(coordinates.speed) : null,
        batteryPercent: null,
        capturedAt: new Date(capturedMs).toISOString(),
      };
      onPosition?.({
        latitude: sample.latitude,
        longitude: sample.longitude,
        accuracyM: sample.accuracyM,
        headingDegrees: sample.headingDegrees,
        speedMps: sample.speedMps,
        capturedAt: sample.capturedAt,
      });
      const queued = await enqueueTrackingSample(session.id, sample);
      setQueueCount(queued.count);
      if (!navigator.onLine) setTrackingState("OFFLINE_QUEUED");
      else if (sample.accuracyM > 150) setTrackingState("LOCATION_UNAVAILABLE");
    } catch (error) {
      setTrackingState(error?.code === "TRACKING_QUEUE_FULL" ? "QUEUE_FULL" : "ERROR");
      stopWatcher();
    }
  }, [onPosition, session, stopWatcher]);

  // One GPS watcher per tracking session. It used to restart whenever the page
  // re-rendered (a new onPosition, any trackingState change), including the
  // moment a Rider pressed a button. On Chromium that restart left the button's
  // own fresh-location request hanging until it timed out ("Getting your current
  // location took too long"), and on a phone every restart costs a new fix.
  // The latest callback is read through a ref; only real changes restart it.
  const enqueueRef = useRef(enqueuePosition);
  useEffect(() => { enqueueRef.current = enqueuePosition; }, [enqueuePosition]);
  const trackingEligible = ["READY", "ACTIVE", "INTERRUPTED"].includes(run?.status) || Boolean(run?.pickupStartedAt);
  const trackingHalted = ["QUEUE_FULL", "SESSION_EXPIRED", "TOKEN_LOST", "STOPPED", "PERMISSION_DENIED", "GPS_UNAVAILABLE", "ERROR"].includes(trackingState);

  useEffect(() => {
    if (!session?.id || !tokenRef.current || !visible || !trackingEligible || trackingHalted) {
      stopWatcher();
      return undefined;
    }
    if (!navigator.geolocation) {
      setTrackingState("GPS_UNAVAILABLE");
      return undefined;
    }
    watchRef.current = navigator.geolocation.watchPosition(
      (position) => {
        enqueueRef.current(position);
      },
      (error) => {
        // Three different things, and they used to read as one. A timeout means
        // the phone has not locked on YET - usually indoors, or under cover -
        // which is not the same as a device that cannot locate itself. Telling a
        // rider their GPS is broken when it is merely slow sends them looking
        // for a fault that is not there.
        if (error?.code === 1) setTrackingState("PERMISSION_DENIED");
        else if (error?.code === 3) setTrackingState("ACQUIRING_FIX");
        else setTrackingState("GPS_UNAVAILABLE");
      },
      // maximumAge was 0, which forbids every cached fix and forces a fresh
      // hardware lock each time - slower to first position and heavier on the
      // battery, for a rider whose location barely changed in five seconds.
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 },
    );
    return () => {
      stopWatcher();
    };
  }, [session?.id, stopWatcher, trackingEligible, trackingHalted, visible]);

  const scheduleRetry = useCallback((flush) => {
    if (retryRef.current.timer || !visible) return;
    const base = Math.min(30000, 2000 * (2 ** retryRef.current.attempt));
    const delay = Math.round(base * (0.8 + Math.random() * 0.4));
    retryRef.current.attempt += 1;
    retryRef.current.timer = setTimeout(() => {
      retryRef.current.timer = null;
      flush();
    }, delay);
  }, [visible]);

  const flush = useCallback(async () => {
    if (!session?.id || !tokenRef.current || flushRef.current || !visible || !navigator.onLine) {
      if (session?.id && !navigator.onLine) setTrackingState("OFFLINE_QUEUED");
      return;
    }
    flushRef.current = true;
    try {
      const rows = await listTrackingSamples(session.id);
      setQueueCount(rows.length);
      if (!rows.length) return;
      setTrackingState(rows.length > 1 ? "REPLAYING" : "STARTING");
      const batch = rows.slice(0, Number(session.policy?.batchMaximum || DEFAULT_POLICY.batchMaximum));
      const response = await riderApi.uploadTrackingLocations(
        businessId, session.id, batch.map((row) => row.sample), tokenRef.current,
      );
      await removeTrackingSamples(session.id, response.results.map((item) => item.sequenceNo));
      lastCommunicationRef.current = Date.now();
      const remaining = await listTrackingSamples(session.id);
      setQueueCount(remaining.length);
      const newest = response.results.at(-1);
      const source = batch.at(-1)?.sample;
      // 75 m is the line between a fix precise enough to place a rider on a road
      // and one that is merely near it. Reporting DEGRADED above it is honest,
      // not a fault: indoors on Wi-Fi a phone genuinely cannot do better.
      if (newest?.status === "ACCEPTED") setTrackingState(source?.accuracyM <= 75 ? "LIVE" : "DEGRADED");
      else if (newest?.rejectionCode === "POOR_ACCURACY") setTrackingState("LOCATION_UNAVAILABLE");
      retryRef.current.attempt = 0;
    } catch (error) {
      if (["TRACKING_SESSION_EXPIRED", "TRACKING_SESSION_INACTIVE", "TRACKING_EXECUTION_AUTHORITY_STALE"].includes(error?.code)) {
        setTrackingState("SESSION_EXPIRED");
        tokenRef.current = null;
        stopWatcher();
      } else if (["SAMPLE_ID_REUSED", "SEQUENCE_REUSED"].includes(error?.code)) {
        setTrackingState("ERROR");
        stopWatcher();
      } else {
        setTrackingState("OFFLINE_QUEUED");
        scheduleRetry(flush);
      }
    } finally {
      flushRef.current = false;
    }
  }, [businessId, scheduleRetry, session, stopWatcher, visible]);

  useEffect(() => {
    if (!session?.id || !tokenRef.current || !visible) return undefined;
    const retryState = retryRef.current;
    const uploadTimer = setInterval(flush, Number(session.policy?.uploadTargetSeconds || DEFAULT_POLICY.uploadTargetSeconds) * 1000);
    const online = () => flush();
    window.addEventListener("online", online);
    flush();
    return () => {
      clearInterval(uploadTimer);
      window.removeEventListener("online", online);
      if (retryState.timer) clearTimeout(retryState.timer);
      retryState.timer = null;
    };
  }, [flush, session, visible]);

  useEffect(() => {
    if (!session?.id || !tokenRef.current || !visible) return undefined;
    const heartbeat = async () => {
      try {
        const response = await riderApi.heartbeatTracking(businessId, session.id, session.version, tokenRef.current);
        lastCommunicationRef.current = Date.now();
        persistSession({ ...session, version: response.version, lastCommunicationAt: lastCommunicationRef.current });
      } catch (error) {
        if (["TRACKING_SESSION_EXPIRED", "TRACKING_SESSION_INACTIVE", "TRACKING_EXECUTION_AUTHORITY_STALE"].includes(error?.code)) {
          setTrackingState("SESSION_EXPIRED");
          stopWatcher();
        } else if (Date.now() - lastCommunicationRef.current >= 120000) {
          setTrackingState("CONNECTION_INTERRUPTED");
        } else setTrackingState("OFFLINE_QUEUED");
      }
    };
    const timer = setInterval(heartbeat, Number(session.policy?.heartbeatTargetSeconds || DEFAULT_POLICY.heartbeatTargetSeconds) * 1000);
    return () => clearInterval(timer);
  }, [businessId, persistSession, session, stopWatcher, visible]);

  const start = async () => {
    // Without run control the server would refuse this, correctly. Sending it
    // anyway produced a 403 on every load of an active target-v2 run and left the
    // card in ERROR, telling the Rider nothing about the actual precondition.
    if (!controlsRun) {
      setTrackingState("AWAITING_DEVICE_CONTROL");
      return;
    }
    setBusy(true);
    setTrackingState("STARTING");
    try {
      const startKey = createClientRequestId("tracking_start");
      const deviceId = getRiderDeviceInstanceId();
      const response = await riderApi.startTracking(businessId, run.id, {
        expectedRunVersion: run.version, deviceInstanceId: deviceId, idempotencyKey: startKey,
      }, ...(Number(run.workflowVersion) === 2 ? [{ executionHeaders: riderExecutionHeaders(businessId, run.id) }] : []));
      const { trackingToken, ...sessionResponse } = response;
      lastCommunicationRef.current = Date.now();
      const meta = { ...sessionResponse, runId: run.id, startKey, initialRunVersion: run.version, deviceInstanceId: deviceId, lastCommunicationAt: lastCommunicationRef.current };
      persistSession(meta);
      if (!trackingToken) {
        tokenRef.current = null;
        setTrackingState("TOKEN_LOST");
      } else {
        tokenRef.current = trackingToken;
      }
    } catch {
      setTrackingState("ERROR");
    } finally {
      setBusy(false);
    }
  };

  const end = async (reason = "RIDER_STOPPED") => {
    if (!session?.id) return;
    setBusy(true);
    try {
      await riderApi.endTracking(businessId, session.id, {
        expectedVersion: session.version, reason,
      });
      await clearTrackingSessionQueue(session.id);
      tokenRef.current = null;
      persistSession(null);
      setQueueCount(0);
      setTrackingState("STOPPED");
      stopWatcher();
    } catch (error) {
      setTrackingState(error?.code === "TRACKING_SESSION_EXPIRED" ? "SESSION_EXPIRED" : "ERROR");
    } finally {
      setBusy(false);
    }
  };

  const recover = async () => {
    if (!controlsRun) {
      setTrackingState("AWAITING_DEVICE_CONTROL");
      return;
    }
    if (!session?.id) return start();
    setBusy(true);
    try {
      const startKey = createClientRequestId('tracking_recover');
      const response = await riderApi.startTracking(businessId,run.id,{
        expectedRunVersion:run.version,deviceInstanceId:session.deviceInstanceId,
        recoverSessionId:session.id,idempotencyKey:startKey,
      }, ...(Number(run.workflowVersion) === 2 ? [{ executionHeaders: riderExecutionHeaders(businessId, run.id) }] : []));
      const {trackingToken,...meta}=response;
      if (!trackingToken) throw new Error('Recovery token unavailable');
      await clearTrackingSessionQueue(session.id);
      tokenRef.current=trackingToken;
      persistSession({...meta,runId:run.id,startKey,initialRunVersion:run.version,deviceInstanceId:session.deviceInstanceId});
      setTrackingState('STARTING');
      setBusy(false);
    } catch {
      setTrackingState("ERROR");
      setBusy(false);
    }
  };

  const autoStarted = useRef(false);
  useEffect(() => {
    if (required && visible && controlsRun && !autoStarted.current) {
      autoStarted.current = true;
      if (!session?.id && !localStorage.getItem(keys.meta)) start();
    }
  });
  const active = Boolean(session?.id && !["STOPPED", "SESSION_EXPIRED"].includes(trackingState));
  // Derived during render: the reason the button is unavailable is a view of
  // whether this device controls the run, not a second copy of that fact.
  const [title, description] = !controlsRun && !active
    ? STATE_COPY.AWAITING_DEVICE_CONTROL
    : (STATE_COPY[trackingState] || STATE_COPY.ERROR);
  useEffect(() => { onTrackingState?.(title); }, [title, onTrackingState]);
  return (
    <section className="rounded-3xl border border-stone-200 bg-white p-5 dark:border-white/10 dark:bg-neutral-900" aria-live="polite">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 gap-3">
          <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-2xl ${trackingState === "LIVE" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-stone-100 text-stone-600 dark:bg-white/10 dark:text-stone-300"}`}>
            {trackingState === "LOCATION_UNAVAILABLE" || trackingState === "GPS_UNAVAILABLE" ? <MapPinOff className="h-5 w-5" aria-hidden="true" /> : <Radio className="h-5 w-5" aria-hidden="true" />}
          </span>
          <div className="min-w-0"><h2 className="font-semibold">Location sharing · {title}</h2><p className="mt-1 text-sm text-stone-600 dark:text-stone-400">{description}</p></div>
        </div>
        {queueCount > 0 && <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">{queueCount} saved</span>}
      </div>
      <p className="mt-4 text-xs leading-relaxed text-stone-500 dark:text-stone-400">Foreground tracking may pause when this screen is hidden, your phone locks, the browser is closed, or your device suspends it.</p>
      <div className="mt-4 flex gap-3">
        {!active && trackingState !== "TOKEN_LOST" && <button type="button" onClick={start} disabled={busy || !controlsRun || !(["READY","ACTIVE","INTERRUPTED"].includes(run.status) || run.pickupStartedAt)} className="inline-flex min-h-11 flex-1 touch-manipulation items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 font-semibold text-white transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:opacity-50 dark:focus-visible:ring-offset-neutral-900">{busy ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <LocateFixed className="h-4 w-4" aria-hidden="true" />} Start sharing</button>}
        {trackingState === "TOKEN_LOST" && <button type="button" onClick={recover} disabled={busy} className="inline-flex min-h-11 flex-1 touch-manipulation items-center justify-center gap-2 rounded-2xl bg-amber-600 px-4 font-semibold text-white transition-colors hover:bg-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 disabled:opacity-50 dark:focus-visible:ring-offset-neutral-900">{busy ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <AlertTriangle className="h-4 w-4" aria-hidden="true" />} Recover session</button>}
        {active && ["PERMISSION_DENIED", "GPS_UNAVAILABLE", "ERROR"].includes(trackingState) && <button type="button" onClick={() => setTrackingState("STARTING")} disabled={busy} className="min-h-11 flex-1 touch-manipulation rounded-2xl bg-emerald-600 px-4 font-semibold text-white transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:opacity-50 dark:focus-visible:ring-offset-neutral-900">Try again</button>}
        {active && !required && trackingState !== "TOKEN_LOST" && <button type="button" onClick={() => end()} disabled={busy} className="inline-flex min-h-11 flex-1 touch-manipulation items-center justify-center gap-2 rounded-2xl border border-stone-300 px-4 font-semibold transition-colors hover:bg-stone-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:opacity-50 dark:border-white/15 dark:hover:bg-white/10 dark:focus-visible:ring-offset-neutral-900">{busy ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Square className="h-4 w-4" aria-hidden="true" />} Stop sharing</button>}
      </div>
    </section>
  );
}
